import {
    InputHTMLAttributes,
    forwardRef,
    ForwardedRef,
    useCallback,
    useId,
    useRef,
    useState,
    useEffect,
    RefCallback,
    MouseEventHandler,
} from 'react';
import classnames from 'classnames';

import { isEventFromInteractiveComponent } from '@hh.ru/magritte-common-mouse';
import { Selection } from '@hh.ru/magritte-common-text-selection';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { Action } from '@hh.ru/magritte-ui-action';
import { FormHelper } from '@hh.ru/magritte-ui-form-helper';
import { CrossOutlinedSize16, CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { IconWrapperComponentSize16, IconWrapperComponentSize24 } from '@hh.ru/magritte-ui-icon/src/iconTypes';
import { BaseInputProps, InputSize } from '@hh.ru/magritte-ui-input/types';
import { useFocus } from '@hh.ru/magritte-ui-input/useFocus';
import { useInputAriaProps } from '@hh.ru/magritte-ui-input/useInputAriaProps';
import { Text } from '@hh.ru/magritte-ui-typography';

import styles from './input.less';

const matchIconSize = (size: InputSize, icon: IconWrapperComponentSize16 | IconWrapperComponentSize24 | undefined) => {
    return icon &&
        (((size === 'medium' || size === 'large') && (icon as IconWrapperComponentSize24)._size === 24) ||
            (size === 'small' && (icon as IconWrapperComponentSize16)._size === 16))
        ? icon
        : null;
};

const BaseInputForwardRefRenderFunc = (
    {
        onChange,
        onFocus,
        onBlur,
        value,
        placeholder,
        disabled = false,
        postfix,
        size = 'medium',
        elevatePlaceholder = false,
        invalid = false,
        description,
        errorMessage,
        formatter,
        buttonIcon: _buttonIcon,
        clearable = false,
        clearableOnFocus,
        onButtonClick,
        buttonDataQa,
        buttonRef,
        inputIcon,
        wrapperRef,
        fixedPlaceholder,
        maxLength,
        hint,
        showHintAlways = false,
        ...restProps
    }: BaseInputProps & Omit<InputHTMLAttributes<HTMLInputElement>, keyof BaseInputProps>,
    ref: ForwardedRef<HTMLInputElement>
) => {
    const wrapperCurrentRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isActionVisible, setActionVisible] = useState(false);
    const inputRefCallback = useMultipleRefs(ref, inputRef);
    const wrapperRefCallback = useMultipleRefs(wrapperRef, wrapperCurrentRef);
    const descriptionId = useId();
    const labelId = useId();
    const hintRef = useRef<HTMLDivElement>(null);
    const hintMaxWidthRef = useRef<{ width: number; text: string } | null>(null);
    const change = useCallback(
        (newValue: string) => {
            if (inputRef.current && formatter) {
                const caretPosition = Selection.getCaretPosition(inputRef.current);
                const formatResult = formatter(value, newValue, caretPosition || 0);
                requestAnimationFrame(() => {
                    if (inputRef.current) {
                        Selection.setCaretPosition(inputRef.current, formatResult.caretPosition);
                    }
                });
                onChange(formatResult.outputValue);
                return;
            }
            onChange(newValue);
        },
        [formatter, onChange, value, inputRef]
    );

    const clearClickHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            onChange('');
            inputRef.current && inputRef.current.focus();
            onButtonClick?.(event);
        },
        [onChange, inputRef, onButtonClick]
    );

    const forceDisabled = useDisabled();
    const isComponentDisabled = forceDisabled || !!disabled;

    const { changeHandler, focusHandler, blurHandler, isFocused } = useFocus(change, onFocus, onBlur);
    const placeholderCanBeElevated = size === 'large' && !!elevatePlaceholder && !!placeholder;
    const [isFocusVisible, setFocusVisible] = useState(false);
    const currentValue = formatter?.('', value, 0).formattedValue || value;
    const buttonQaLabel = buttonDataQa || `input-${clearable ? 'clearable-' : ''}button`;
    const ariaProps = useInputAriaProps({
        invalid,
        hasDescription: !!description,
        hasPlaceholder: !!placeholder,
        descriptionId,
        labelId,
    });

    const handleWrapperFocus = () => {
        setActionVisible(true);
    };

    const handleWrapperBlur = () => {
        requestAnimationFrame(() => {
            if (!wrapperCurrentRef.current?.contains(document.activeElement)) {
                setActionVisible(false);
            }
        });
    };

    const onClickWrapper: MouseEventHandler = (e) => {
        if (!isEventFromInteractiveComponent(e)) {
            inputRef.current?.focus();
        }
    };

    const focusedOrFilled = isFocused || value !== '';
    const placeholderIsElevated = focusedOrFilled && placeholderCanBeElevated;
    const showPostfix = !!postfix && (placeholderIsElevated || value !== '');
    const showFixedPlaceholder = focusedOrFilled && !!fixedPlaceholder;
    const showLabel = placeholderCanBeElevated || (value === '' && !showFixedPlaceholder);

    const InputIconComponent = matchIconSize(size, inputIcon);
    const showClearButton = value.length > 0 && (clearable || (!!clearableOnFocus && isActionVisible));
    const showHint = !!hint && (showHintAlways || value.length > 0);
    const buttonIcon = matchIconSize(size, _buttonIcon);

    const hintWrapperRefCallback: RefCallback<HTMLDivElement> = useCallback(
        (element) => {
            if (element && hintRef.current) {
                const text = hintRef.current.innerText;
                if (text.length > 30) {
                    let maxWidth;
                    if (text !== hintMaxWidthRef.current?.text) {
                        const newText = text.slice(0, 30);
                        hintRef.current.innerText = newText;
                        maxWidth = hintRef.current.offsetWidth;
                        hintRef.current.innerText = text;
                        hintMaxWidthRef.current = {
                            width: maxWidth,
                            text,
                        };
                    } else {
                        maxWidth = hintMaxWidthRef.current.width;
                    }

                    element.style.maxWidth = `${maxWidth}px`;
                }
            }
        },
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
        [hint]
    );

    useEffect(() => {
        if (inputRef.current) {
            setFocusVisible(isFocused && inputRef.current.classList.contains('focus-visible'));
        }
    }, [isFocused]);

    return (
        <div
            ref={wrapperRefCallback}
            className={classnames(styles.input, {
                [styles.focused]: isFocused,
                [styles.focusVisible]: isFocusVisible,
                [styles.disabled]: isComponentDisabled,
                [styles.large]: size === 'large',
                [styles.small]: size === 'small',
                [styles.elevated]: placeholderCanBeElevated,
                [styles.invalid]: !!invalid,
                [styles.withIcon]: !!inputIcon,
            })}
            onFocus={clearableOnFocus ? handleWrapperFocus : undefined}
            onBlur={clearableOnFocus ? handleWrapperBlur : undefined}
            onClick={onClickWrapper}
            data-interactive={true}
        >
            <div className={styles.positionContainer}>
                <div className={styles.hintContainer}>
                    <div className={styles.autofillHelper} />
                    <div className={styles.contentContainer}>
                        {!!InputIconComponent && (
                            <div className={styles.icon}>
                                <InputIconComponent />
                            </div>
                        )}
                        <div className={styles.valueContainer}>
                            <input
                                {...restProps}
                                {...ariaProps}
                                maxLength={maxLength}
                                disabled={isComponentDisabled}
                                suppressHydrationWarning
                                className={styles.field}
                                value={currentValue}
                                onInput={changeHandler} // maskito требует onInput, (https://maskito.dev/frameworks/react) который работает аналогично onChange
                                onFocus={focusHandler}
                                onBlur={blurHandler}
                                ref={inputRefCallback}
                            />
                            <div className={styles.additionalValue}>
                                <Text
                                    Element="span"
                                    typography={size === 'small' ? 'label-3-regular' : 'label-2-regular'}
                                >
                                    <span className={styles.valueGhost}>{currentValue}</span>
                                    {showFixedPlaceholder && (
                                        <span className={styles.fixedPlaceholder}>
                                            {fixedPlaceholder.slice(value.length)}
                                        </span>
                                    )}
                                    {showPostfix && (
                                        <>
                                            {'\u00A0'}
                                            {postfix}
                                        </>
                                    )}
                                </Text>
                            </div>
                        </div>
                        <div className={styles.riggingContainer}>
                            {showLabel && (
                                <label
                                    className={classnames(styles.label, {
                                        [styles.labelElevated]: placeholderIsElevated,
                                    })}
                                    id={ariaProps['aria-labelledby']}
                                >
                                    {placeholder}
                                </label>
                            )}
                        </div>
                    </div>
                    {showHint && (
                        <div className={styles.hint} ref={hintWrapperRefCallback} data-qa="input-hint">
                            <Text Element="span" typography={size === 'small' ? 'label-4-regular' : 'label-3-regular'}>
                                <span ref={hintRef}>{hint}</span>
                            </Text>
                        </div>
                    )}
                </div>
                {(buttonIcon || showClearButton) && (
                    <div className={styles.rightContainer}>
                        {(buttonIcon || showClearButton) && (
                            <Action
                                data-qa={buttonQaLabel}
                                mode="secondary"
                                onMouseDown={(event) => event.preventDefault()} // чтобы инпут не терял фокус
                                onClick={showClearButton ? clearClickHandler : (onButtonClick as () => void)}
                                icon={
                                    showClearButton
                                        ? (size === 'small' && CrossOutlinedSize16) || CrossOutlinedSize24
                                        : (buttonIcon as IconWrapperComponentSize16 | IconWrapperComponentSize24)
                                }
                                disabled={isComponentDisabled}
                                ref={buttonRef}
                            />
                        )}
                    </div>
                )}
            </div>
            <FormHelper
                description={description}
                errorMessage={errorMessage}
                descriptionId={ariaProps['aria-describedby']}
                disabled={isComponentDisabled}
                invalid={invalid}
            />
        </div>
    );
};

const BaseInput = forwardRef(BaseInputForwardRefRenderFunc);

BaseInput.displayName = 'BaseInput';

export { BaseInput };
